import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { Link, Skeleton } from '@boss/ui';
import { getAccountPageSlugByType } from '@boss/utils';

import { useAccountDiscountInfoDetail } from '../../../../client-queries';
import { getVisibleToDate } from '../../../../utils';

type Props = {
  discountGroupIds?: string[];
  discountKey: string;
};

const DiscountDetail = ({ discountGroupIds, discountKey }: Props) => {
  const { locale, query } = useRouter();
  const { t } = useTranslation('account');

  const localeStr = locale as string;
  const baseSlug = getAccountPageSlugByType('DISCOUNT', localeStr) ?? '';

  const sectionTitleMap: Record<string, string> = {
    '1': t('discount.default'),
    '2': t('discount.specific'),
    '3': t('discount.worksite'),
  };

  const keyPrefix = discountKey.split(';')[0];
  const keyName = discountKey.includes(';') ? discountKey.split(';').slice(1).join(';') : discountKey;

  const pageTitle = query.title ? decodeURIComponent(query.title as string) : keyName;
  const sectionTitle = query.sectionTitle
    ? decodeURIComponent(query.sectionTitle as string)
    : sectionTitleMap[keyPrefix] ?? undefined;

  const { data: lines, isLoading } = useAccountDiscountInfoDetail(localeStr, discountKey, discountGroupIds);

  return (
    <div className="mb-20">
      <Link href={baseSlug} locale={localeStr}>
        ← {t('discount.backToOverview')}
      </Link>
      {sectionTitle && <h2 className="mb-9">{sectionTitle}</h2>}
      <h3 className="mb-14 text-2xl">{pageTitle}</h3>
      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {lines?.map((line, index) => {
            const visibleDate = getVisibleToDate(line.toDate);

            return (
              <div className="rounded-2 relative bg-white p-5 shadow-lg" key={line.sortKey ?? index}>
                {line.volumeDeal && (
                  <span className="bg-yellow text-brown absolute right-0 top-4 rounded-bl-lg rounded-tl-[1px] px-2 py-1 text-sm font-bold capitalize">
                    {t('discount.volumeDeal')}
                  </span>
                )}
                <p className="text-base font-bold">{line.title}</p>
                {line.subtitle && <p className="mt-1 text-sm">{line.subtitle}</p>}
                <div className="mt-2">
                  {line.discountInfo.map((info, i) => (
                    <p className="text-red text-sm font-bold" key={i}>
                      {info}
                    </p>
                  ))}
                </div>
                {visibleDate && <p className="mt-2 text-sm">{t('discount.validUntil', { date: visibleDate })}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DiscountDetail;
